/*
 * User-facing theme editor metadata derived from Schema V2.
 *
 * External LVGL 7 themes use short property ids (bc, bw, tc, ...), while
 * openHASP-next's Schema V2 uses descriptive property names and typed metadata.
 * This adapter keeps the JSONL wire format lossless but presents the editor
 * with the same labels and control types as the rest of the designer.
 */

import { objectSchemaV2 } from '../Hasp/properties/v2/index.js';
import { COLOR_PROPERTIES, FIRMWARE_STYLE_NAMES, PROPERTIES } from './themeModel.js';

const WIRE_TO_SCHEMA = {
    bc: 'bg_color', bgc: 'bg_grad_color', bgd: 'bg_grad_dir', bo: 'bg_opa',
    bco: 'border_color', bw: 'border_width', bop: 'border_opa', bs: 'border_side',
    bp: 'border_post', cw: 'clip_corner', r: 'radius',
    oc: 'outline_color', ow: 'outline_width', oo: 'outline_opa', op: 'outline_pad',
    shc: 'shadow_color', shw: 'shadow_width', shs: 'shadow_spread',
    tc: 'text_color', tf: 'text_font', vc: 'value_color', vf: 'value_font',
    lc: 'line_color', lw: 'line_width', lr: 'line_rounded',
    irc: 'image_recolor',
    pl: 'pad_left', pr: 'pad_right', pt: 'pad_top', pb: 'pad_bottom', pn: 'pad_inner',
    ml: 'margin_left', mr: 'margin_right', mt: 'margin_top', mb: 'margin_bottom',
    swid: 'scale_width', seb: 'scale_border_width', sew: 'scale_line_width',
    sgc: 'scale_grad_color', sec: 'scale_end_color',
    tt: 'transition_time', td: 'transition_delay',
    pi: 'pattern', po: 'pattern_opa', prc: 'pattern_recolor',
};

const LABELS = {
    bc: 'Background color', bgc: 'Background gradient color', bgd: 'Background gradient direction',
    bo: 'Background opacity', bco: 'Border color', bw: 'Border width', bop: 'Border opacity',
    bs: 'Border sides', bp: 'Border post', cw: 'Clip corners', r: 'Corner radius',
    oc: 'Outline color', ow: 'Outline width', oo: 'Outline opacity', op: 'Outline padding',
    shc: 'Shadow color', shw: 'Shadow width', shs: 'Shadow spread',
    tc: 'Text color', tf: 'Text font', vc: 'Value color', vf: 'Value font',
    lc: 'Line color', lw: 'Line width', lr: 'Rounded line', irc: 'Image recolor',
    pl: 'Padding left', pr: 'Padding right', pt: 'Padding top', pb: 'Padding bottom', pn: 'Inner padding',
    ml: 'Margin left', mr: 'Margin right', mt: 'Margin top', mb: 'Margin bottom',
    swid: 'Scale width', seb: 'Scale border width', sew: 'Scale line width',
    sgc: 'Scale gradient color', sec: 'Scale end color',
    tt: 'Transition time', td: 'Transition delay', pi: 'Pattern', po: 'Pattern opacity', prc: 'Pattern recolor',
};

const FALLBACK_TYPES = {
    bgd: { type: 'enum', values: ['none', 'hor', 'ver'] },
    bp: { type: 'bool' }, cw: { type: 'bool' }, lr: { type: 'bool' },
    tf: { type: 'enum', values: ['small', 'normal', 'subtitle', 'title'] },
    vf: { type: 'enum', values: ['small', 'normal', 'subtitle', 'title'] },
    pi: { type: 'enum', values: ['ok'] },
    tr1: { type: 'string' }, tr2: { type: 'string' }, tr3: { type: 'string' },
    tr4: { type: 'string' }, tr5: { type: 'string' }, tr6: { type: 'string' },
};

// Keep the add-style picker in lockstep with the firmware wire catalog. This
// prevents the editor from offering names that the firmware cannot load.
const STYLE_CATALOG = [...FIRMWARE_STYLE_NAMES];

const allSchemaProperties = new Map();
for (const schema of Object.values(objectSchemaV2)) {
    for (const part of Object.values(schema.parts ?? {})) {
        for (const [name, metadata] of Object.entries(part.properties ?? {})) {
            if (!allSchemaProperties.has(name)) allSchemaProperties.set(name, metadata);
        }
    }
}

function humanize(value) {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function definitionFor(key) {
    const schemaName = WIRE_TO_SCHEMA[key];
    const schemaMeta = schemaName ? allSchemaProperties.get(schemaName) : null;
    const fallback = FALLBACK_TYPES[key] ?? {};
    const definition = {
        key,
        label: LABELS[key] ?? humanize(schemaName ?? key),
        type: COLOR_PROPERTIES.has(key) ? 'color' : (fallback.type ?? schemaMeta?.type ?? 'int'),
        min: schemaMeta?.min,
        max: schemaMeta?.max,
        values: fallback.values ?? schemaMeta?.values,
        semanticName: schemaName ?? key,
    };
    if (key === 'bgd') definition.values = ['none', 'hor', 'ver'];
    if (['bo', 'bop', 'oo', 'po'].includes(key)) { definition.min = 0; definition.max = 255; }
    if (['bw', 'lw', 'shw', 'shs', 'swid', 'seb', 'sew', 'sz'].includes(key)) definition.min = 0;
    return definition;
}

export const THEME_PROPERTY_DEFINITIONS = Object.fromEntries(
    PROPERTIES.map((key) => [key, definitionFor(key)]),
);

export { STYLE_CATALOG };

export function themePropertyDefinition(key) {
    return THEME_PROPERTY_DEFINITIONS[key] ?? definitionFor(key);
}
